import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Login() {
  const { user, demoLogin } = useAuth();
  const nav = useNavigate();
  const [role, setRole] = useState("sales_manager");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) nav("/board", { replace: true });
  }, [user, nav]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/board";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleDemo = async () => {
    setBusy(true);
    try {
      await demoLogin(role);
      toast.success(`Signed in as demo ${role.replace("_", " ")}`);
      nav("/board", { replace: true });
    } catch (e) {
      toast.error("Demo login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-[#0A0A0A] text-white overflow-hidden">
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1769546253924-9e23d794be53?crop=entropy&cs=srgb&fm=jpg&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "grayscale(0.5) contrast(1.1)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/95 to-[#0A0A0A]" />
      <div className="absolute inset-0 diag-stripe" />

      <div className="relative z-10 min-h-screen flex flex-col">
        <header className="px-8 py-6 flex items-center justify-between border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-[#34C759] rounded-full" />
            <span className="font-display text-xl font-black uppercase tracking-widest">THE BE BACK</span>
          </div>
          <span className="text-xs uppercase tracking-[0.3em] text-white/40">Dealership Ops</span>
        </header>

        <main className="flex-1 grid lg:grid-cols-2 max-w-[1400px] mx-auto w-full px-8 py-12 gap-12 items-center">
          <div className="space-y-8" data-testid="login-hero">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#34C759]/40 bg-[#34C759]/5">
              <span className="w-1.5 h-1.5 bg-[#34C759] rounded-full animate-pulse" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#34C759]">Live Floor Operations</span>
            </div>
            <h1 className="font-display text-6xl sm:text-7xl font-black uppercase tracking-tight leading-[0.9]">
              Run Your<br />
              <span className="text-[#34C759]">Showroom</span><br />
              Like A Pit Crew.
            </h1>
            <p className="text-base text-white/60 max-w-md leading-relaxed">
              The position queue, the activity board, and the live scoreboard. One screen. Zero chaos.
              Built for the sales tower, the manager's pocket, and the salesperson on the lot.
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              {["Position Queue", "Activity Lock", "Power Rotation", "T.O. Alerts", "Live Scoreboard"].map(t => (
                <span key={t} className="text-[10px] uppercase tracking-[0.25em] px-2.5 py-1 border border-white/10 text-white/50">{t}</span>
              ))}
            </div>
          </div>

          <div className="bg-[#141414] border border-white/10 p-8 max-w-md w-full ml-auto" data-testid="login-card">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">Sign in</div>
            <h2 className="font-display text-3xl font-black uppercase tracking-wide mb-6">Take The Floor</h2>

            <Button
              onClick={handleGoogle}
              className="w-full h-12 bg-white text-black hover:bg-white/90 font-bold uppercase tracking-wider rounded-sm"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 5c1.6 0 3 .55 4.1 1.6l3-3C17.05 1.95 14.7 1 12 1 7.4 1 3.45 3.65 1.55 7.5l3.5 2.7C5.95 7.5 8.7 5 12 5z"/><path fill="#4285F4" d="M23 12.27c0-.83-.07-1.62-.2-2.4H12v4.55h6.2c-.27 1.4-1.07 2.6-2.3 3.4l3.55 2.75c2.05-1.9 3.55-4.7 3.55-8.3z"/><path fill="#FBBC05" d="M5.05 14.2A6.97 6.97 0 0 1 4.7 12c0-.77.13-1.52.35-2.2l-3.5-2.7A11 11 0 0 0 1 12c0 1.78.43 3.46 1.2 4.95l2.85-2.75z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.97 7.28-2.65L15.7 17.6c-1 .68-2.27 1.1-3.7 1.1-3.3 0-6.05-2.5-7.05-5.85l-3.5 2.7C3.45 19.4 7.4 23 12 23z"/></svg>
              Sign in with Google
            </Button>

            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-white/30">Or try as</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <div className="space-y-3">
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 h-11 rounded-sm" data-testid="demo-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-white/10">
                  <SelectItem value="gsm">General Sales Manager</SelectItem>
                  <SelectItem value="sales_manager">Sales Manager</SelectItem>
                  <SelectItem value="floor_manager">Floor Manager</SelectItem>
                  <SelectItem value="salesperson">Salesperson</SelectItem>
                  <SelectItem value="bdc">BDC Agent</SelectItem>
                </SelectContent>
              </Select>
              <Button
                onClick={handleDemo}
                disabled={busy}
                className="w-full h-12 bg-[#34C759] text-black hover:bg-[#2bb850] font-bold uppercase tracking-wider rounded-sm"
                data-testid="demo-login-btn"
              >
                {busy ? "Loading…" : "Enter Demo"}
              </Button>
            </div>

            <p className="text-[10px] uppercase tracking-[0.2em] text-white/30 mt-6 text-center">
              Demo loads pre-seeded dealership · Apex Motors
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
